/* ------------------------------------------------------------------
   /frontend/src/pages/dashboard/post/twitter/TwitterPostService.js
   v2.1 - Upgraded with comprehensive, user-friendly error handling
   to match the mobile app's level of detail.
------------------------------------------------------------------- */

import axios from 'axios';

/**
 * publishTwitterPost
 * ────────────────────────────────────────────────────────────
 * @param {string}   twitterId   Twitter user / account ID (numeric string)
 * @param {string}   message     Tweet text
 * @param {string}   [mediaUrl]  Publicly-accessible URL to one image / video
 * @returns {Promise<object>}    The JSON response from your backend on success.
 * @throws {Error}               Throws a new Error with a user-friendly message on failure.
 */
export async function publishTwitterPost(twitterId, message = '', mediaUrl = '') {
  try {
    if (!twitterId) throw new Error('Twitter/X ID is required');

    /* Build request body; only include mediaUrl when it’s provided */
    const body = { twitterId, message };
    if (mediaUrl) body.mediaUrl = mediaUrl;

    const { data } = await axios.post('/api/posting/twitter/publish', body, {
      withCredentials: true // Preserves session cookies for web
    });

    return data;
  } catch (err) {
    console.error('[Twitter Web Service] Error:', err.response ? err.response.data : err.message);
    
    // --- START: Comprehensive Error Parsing Logic ---
    let errorMessage = 'Failed to post to Twitter/X.';
    
    if (!err.response && err.message) {
      // Handle client-side validation errors
      errorMessage = err.message;
    } else if (err.response?.data) {
      const errorData = err.response.data;
      
      // Check for various error formats from your backend or directly from Twitter API
      if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.error?.message) {
        errorMessage = errorData.error.message;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details) {
        errorMessage = errorData.details;
      } else if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors[0]) {
        // Handle the detailed Twitter API v2 error format (array of errors)
        const firstError = errorData.errors[0];
        const code = firstError.code;
        const twitterMessage = firstError.message || firstError.detail || '';
          
        switch(code) {
          case 32: errorMessage = 'Twitter/X authentication failed. Please reconnect your account.'; break;
          case 34: errorMessage = 'Twitter/X page does not exist.'; break;
          case 64: errorMessage = 'Twitter/X account suspended. Please check your account status.'; break;
          case 88: errorMessage = 'Twitter/X rate limit exceeded. Please wait 15 minutes.'; break;
          case 89: errorMessage = 'Twitter/X access token expired. Please reconnect your account.'; break;
          case 99: errorMessage = 'Twitter/X OAuth credentials invalid. Please reconnect.'; break;
          case 135: errorMessage = 'Twitter/X authentication error. Please try again.'; break;
          case 170: errorMessage = 'Twitter/X post is empty. Please add text or media.'; break;
          case 186: errorMessage = 'Twitter/X post too long. Maximum 280 characters allowed.'; break;
          case 187: errorMessage = 'Duplicate post. This content was already posted to Twitter/X.'; break;
          case 195: errorMessage = 'Twitter/X missing parameters. Please check your content.'; break;
          case 220: errorMessage = 'Twitter/X credentials invalid. Please reconnect your account.'; break;
          case 226: errorMessage = 'Twitter/X detected automated behavior. Please try again later.'; break;
          case 261: errorMessage = 'Twitter/X application cannot perform write actions.'; break;
          case 326: errorMessage = 'Twitter/X account temporarily locked. Please verify your account.'; break;
          case 354: errorMessage = 'Twitter/X post too long. Please shorten your message.'; break;
          case 403: errorMessage = 'Twitter/X posting limit reached. Please try again later.'; break;
          case 416: errorMessage = 'Twitter/X app no longer active. Please contact support.'; break;
          case 417: errorMessage = 'Twitter/X app suspended. Please contact support.'; break;
          case 425: errorMessage = 'Twitter/X media upload failed. Please try a different file.'; break;
          case 431: errorMessage = 'Twitter/X media file too large. Maximum 15MB for images, 512MB for videos.'; break;
          case 432: errorMessage = 'Twitter/X video duration too long. Maximum 140 seconds.'; break;
          case 433: errorMessage = 'Twitter/X invalid media format. Use JPG, PNG, GIF, or MP4.'; break;
          default: errorMessage = twitterMessage || `Twitter/X error (code: ${code})`;
        }
      } else if (errorData.error_description) {
        errorMessage = errorData.error_description;
      } else if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.title) {
        errorMessage = errorData.title;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
    } else if (err.response?.status) {
      // Fallback for generic HTTP status codes
      switch (err.response.status) {
        case 400: errorMessage = 'Invalid Twitter/X request. Please check your content.'; break;
        case 401: errorMessage = 'Twitter/X authentication failed. Please reconnect your account.'; break;
        case 403: errorMessage = 'Twitter/X access forbidden. Check your account permissions.'; break;
        case 404: errorMessage = 'Twitter/X resource not found. Account may be deleted.'; break;
        case 429: errorMessage = 'Twitter/X rate limit exceeded. Please wait before posting again.'; break;
        case 500: case 502: case 503: errorMessage = 'Twitter/X server error. Please try again later.'; break;
        default: errorMessage = `An unexpected error occurred (Status: ${err.response.status}).`;
      }
    } else if (err.message) {
      // Fallback for network errors etc.
      errorMessage = err.message;
    }
    // --- END: Comprehensive Error Parsing Logic ---

    // Throw a new error with the clean, user-friendly message.
    // The calling component (`Post.js`) will catch this and show it in an alert.
    throw new Error(errorMessage);
  }
}
